// Copyright (c) Microsoft Corporation.
// Licensed under the MIT license.

import { Action, Dispatch } from "redux";
import size from "lodash/size";
import ProjectService from "../../services/projectService";
import AnalysisService from "../../services/analysisService"
import CreateModelService from '../../services/createModelService'
import { ActionTypes } from "./actionTypes";
import { AssetService } from "../../services/assetService";
import {
    AppError,
    ErrorCode,
    IApplicationState,
    IAsset,
    IAssetMetadata,
    IProject,
    ITag,
    ITableTag,
    IUploadFilesDetails,
    IOcrContent,IAzureCVExportFields,
    IAzureFields,
} from "../../models/applicationState";
import { generateKey } from "../../common/crypto";
import { createAction, createPayloadAction, IPayloadAction } from "./actionCreators";
import { appInfo } from "../../common/appInfo";
import { shortid } from 'shortid';

import { strings } from "../../common/strings";
import { ExportProviderFactory } from "../../providers/export/exportProviderFactory";
import { ExportAssetState, IExportResults } from "../../providers/export/exportProvider";

//new
import {AzureCustomVisionProvider,NewOrExisting,AzureRegion,IAzureCustomVisionExportOptions}  from "../../providers/export/azureCustomVision";
import { IExportFormat } from "vott-react";
import JsonViewResponse from "../../react/components/pages/testUploadFiles/JsonViewResponse";


/**
 * Actions to be performed in relation to projects
 */
export default interface IProjectActions {
    createNewModel(modelObject: any): Promise<void>;
    getOcrParticularFile(fileObject: any, project: IProject): Promise<IOcrContent>;
    getAnalyseResult(projectName: string, analyse: string): Promise<any>;
    generateOcrForSingleFile(project: IProject, fileName: string, asset: IAsset): Promise<void>;
    loadBlobs(project: IProject, keyword: string): Promise<IAsset[]>;
    loadTestBlobs(project: IProject, keyword: string): Promise<any>;
    loadProject(project: IProject): Promise<IProject>;
    saveProject(project: IProject, saveTags?: boolean, updateTagsFromFiles?: boolean): Promise<IProject>;
    deleteProject(project: IProject): Promise<void>;
    closeProject(): void;
    loadAssets(project: IProject): Promise<IAsset[]>;
    loadAssetMetadata(project: IProject, asset: IAsset): Promise<IAssetMetadata>;
    saveAssetMetadata(project: IProject, assetMetadata: IAssetMetadata): Promise<IAssetMetadata>;
    updateProjectTag(project: IProject, oldTag: ITag, newTag: ITag): Promise<IAssetMetadata[]>;
    updateProjectTableTag(project: IProject, oldTag: ITableTag, newTag: ITableTag): Promise<IAssetMetadata[]>;
    deleteProjectTag(project: IProject, tagName): Promise<IAssetMetadata[]>;
    deleteTagFromFile(project: IProject, tagName, asset: IAsset): Promise<IAssetMetadata[]>;
    updateProjectTagsFromFiles(project: IProject, asset?: string): Promise<void>;
    updateProjectTagsFromExistingLabelFile(project: IProject, asset?: string): Promise<void>;
    clearStoredExistingModelOcrFiles(): Promise<void>;
    deleteAsset(asset: IAsset): Promise<void>;
    updateAssetWithImageUri(asset: IAsset, imageUri: string): Promise<void>;
    deleteAssetFR(project: IProject, assetMetadata: IAssetMetadata): Promise<void>;
    updatedAssetMetadata(project: IProject, assetDocumentCountDifference: any): Promise<void>;

 
    //new
    exportProject(project: IProject): Promise<void> | Promise<IExportResults>;
    updateProjectcv(project: IProject, cvProjId: string, cvProjName: string): Promise<IProject>;
    saveTrainedProject(project: IProject,TrainedData?: any): Promise<IProject>;
  
    loadOpBlobs(project: IProject): Promise<IAsset[]>;

    loadODAssets(project: IProject,assets:IAsset[]): Promise<IAsset[]>;
    clearTestBlobs():void;
    loadBlobsbyProject(project: IProject, keyword: string): Promise<IAssetMetadata>;

    addAssetToProject(project: IProject, fileName: string, buffer: Buffer, analyzeResult: any): Promise<IAsset>;

}


export function getUploadedDocsData(uploadedDoc: IUploadFilesDetails[]): (dispatch: Dispatch) => Promise<void> {
    return async (dispatch: Dispatch) => {
        dispatch(uploadedDocsData(uploadedDoc));
    };
}

export function clearStoredExistingModelOcrFiles(): (dispatch: Dispatch) => Promise<void> {
    return async (dispatch: Dispatch) => {
        dispatch(clearExistiongOcrFiles());
        dispatch(clearExistingTestFiles())
    };
}


export function updateAssetWithImageUri(asset: IAsset, imageUri: string): (dispatch: Dispatch) => Promise<void> {
    return async (dispatch: Dispatch) => {
         const assetWithImageUri = { asset, imageUri };
         dispatch(updateAssetWithImageUriAction(assetWithImageUri))
    };
}

export function getOcrParticularFile(fileObject: any, project: IProject): (dispatch: Dispatch,  getState: () => IApplicationState) => Promise<IOcrContent> {
    // const assets = await assetService.getAssetsFromList(element);
    return async (dispatch: Dispatch, getState: () => IApplicationState) => {
        const appState = getState();
        const ocrFile = await appState.selectedOcrFiles.find((ocrFile) => ocrFile.fileName === fileObject.file_name);
        let ocrContent;
        if (!ocrFile) {
        const projectService = new ProjectService();
        ocrContent = await projectService.getOcrForIndividualFile(fileObject);
        const assetService = new AssetService(project);
        const updatedAsset = await assetService.getAssetsFromList(fileObject.asset,  "doneOcr");
        dispatch(updateAssetState(updatedAsset));
        dispatch(loadOcrContent(ocrContent));
        } else {
            ocrContent = ocrFile;
        }
        return ocrContent;
    };
}

export function getAnalyseResult(projectName: string, analyse: string): (dispatch: Dispatch) => Promise<any> {
    return async (dispatch: Dispatch) => {
        const analysisService = new AnalysisService();
        const analyseData = await analysisService.getAnalyseResultData(projectName, analyse);
        return analyseData;
    };
}

export function createNewModel(modelObject: any): (dispatch: Dispatch) => Promise<void> {
    return async (dispatch: Dispatch) => {
        const modelService = new CreateModelService();
        const createModel = await modelService.createModelName(modelObject);
        return createModel;
    };
}

/**
 * Dispatches Load Project action and resolves with IProject
 * @param project - Project to load
 */
export function loadProject(project: IProject):
    (dispatch: Dispatch, getState: () => IApplicationState) => Promise<IProject> {
    return async (dispatch: Dispatch, getState: () => IApplicationState) => {
        const appState = getState();
        const projectService = new ProjectService();

        // Lookup security token used to decrypt project settings
        const projectToken = appState.appSettings.securityTokens
            .find((securityToken) => securityToken.name === project.securityToken);

        if (!projectToken) {
            throw new AppError(ErrorCode.SecurityTokenNotFound, "Security Token Not Found");
        }
        const loadedProject = await projectService.load(project, projectToken);

        dispatch(loadProjectAction(loadedProject));
        return loadedProject;
    };
}




export function loadBlobsbyProject(project: IProject,keyword: string):
(dispatch: Dispatch, getState: () => IApplicationState) => Promise<IAssetMetadata> {
    return async (dispatch: Dispatch, getState: () => IApplicationState) => {
        const appState = getState();
        // debugger;
        const projectService = new ProjectService();
        const loadFiles =  projectService.loadAllFilesFromBlob(project.name, keyword);

        console.log("load blobs-load files==>"+JSON.stringify(loadFiles));
        const stateAsset =  appState.loadAllFiles[0];
        const assetsData = [];
        assetsData.push(stateAsset);
        const assetService = new AssetService(project);
        const assets =  await assetService.getAssetsFromList(stateAsset);
       
        const assetMetadata = await  assetService.getAssetMetadata(assets);
      
        return { ...assetMetadata };

       // assetsData.push(assets);
       //const connection = { id: null, name: null, providerType: "azureBlobStorage", providerOptions: {} };
       // connection.providerOptions["sas"] = IAzureFields.sasUrl;
       /*
        const assetService = new AssetService(project);
        const assetsData = [];
        if (keyword === "training") {
        loadFiles.forEach(async (element: any) => {
            const stateAsset = await appState.loadAllFiles[0];//.find((file) => file.name === element.name);
            if (!stateAsset) {
            const assets = await assetService.getAssetsFromList(element);
            assetsData.push(assets);
           // dispatch(loadAllBlobFiles(assets));
        } else {
          //  assetsData.push(stateAsset);
           // dispatch(loadAllBlobFiles(stateAsset));
        }
        });
    }     
    else if (keyword === "OPtesting") {
console.log("op files count:"+size(loadFiles));

        loadFiles.forEach(async (element: any) => {
            const stateAsset = await appState.loadOPTestFiles.find((file) => file.name === element.name);
            if (!stateAsset) {
            const assets = await assetService.getAssetsFromList(element);
            assetsData.push(assets);
            dispatch(loadOPTestAllFiles(assets));
        } else {
            assetsData.push(stateAsset);
            dispatch(loadOPTestAllFiles(stateAsset));
        }
        });
    } 
    else {
        loadFiles.forEach(async (element: any) => {
            const stateAsset = appState.loadTestFiles.find((file) => file.name === element.name);
            if (!stateAsset) {
            const assets = await assetService.getAssetsFromList(element);
            assetsData.push(assets);
            dispatch(loadTestAllFiles(assets));
        } else {
            assetsData.push(stateAsset);
            dispatch(loadTestAllFiles(stateAsset));
        }
        });
    }*/
      //  return assets;
    };
}

export function loadBlobs(project: IProject, keyword: string):
(dispatch: Dispatch, getState: () => IApplicationState) => Promise<IAsset[]> {
    return async (dispatch: Dispatch, getState: () => IApplicationState) => {
        const appState = getState();
        // debugger;
        const projectService = new ProjectService();
        const loadFiles = await projectService.loadAllFilesFromBlob(project.name, keyword);

        console.log("load blobs-load files==>"+JSON.stringify(loadFiles));
    
        const assetService = new AssetService(project);
        const assetsData = [];
        if (keyword === "invtraining") {
            loadFiles.forEach(async (element: any) => {
                const stateAsset = await appState.loadAllFiles.find((file) => file.name === element.name);
                if (!stateAsset) {
                const assets = await assetService.getAssetsFromList(element);
                assetsData.push(assets);
                dispatch(loadAllBlobFiles(assets));
            } else {
                assetsData.push(stateAsset);
                dispatch(loadAllBlobFiles(stateAsset));
            }
            });
        } 
        else if (keyword === "training") {
        loadFiles.forEach(async (element: any) => {
            const stateAsset = await appState.loadAllFiles.find((file) => file.name === element.name);
            if (!stateAsset) {
            const assets = await assetService.getAssetsFromList(element);
            assetsData.push(assets);
            dispatch(loadAllBlobFiles(assets));
        } else {
            assetsData.push(stateAsset);
            dispatch(loadAllBlobFiles(stateAsset));
        }
        });
    } 
    else if (keyword === "FailedProcess") {

        if(loadFiles.length>0)
        {
       const loadNFiles = [loadFiles[0]];

loadNFiles.forEach(async (element: any) => {
    // debugger;
        const assets = await assetService.getAssetsFromList(loadFiles[0]);
            assetsData.push(assets);
            dispatch(loadAllBlobFiles(assets));
});
        }
            /*
        loadNFiles.forEach(async (element: any) => {
            const stateAsset = await appState.loadAllFiles.find((file) => file.name === element.name);
            if (!stateAsset) {
            const assets = await assetService.getAssetsFromList(element);
            assetsData.push(assets);
            dispatch(loadAllBlobFiles(assets));
        } else {
            assetsData.push(stateAsset);
            dispatch(loadAllBlobFiles(stateAsset));
        }
        });*/
    }         
    else if (keyword === "OPtesting") {
console.log("op files count:"+size(loadFiles));

        loadFiles.forEach(async (element: any) => {
            const stateAsset = await appState.loadOPTestFiles.find((file) => file.name === element.name);
            if (!stateAsset) {
            const assets = await assetService.getAssetsFromList(element);
            assetsData.push(assets);
            dispatch(loadOPTestAllFiles(assets));
        } else {
            assetsData.push(stateAsset);
            dispatch(loadOPTestAllFiles(stateAsset));
        }
        });
    } 
    else {
        loadFiles.forEach(async (element: any) => {
            const stateAsset = appState.loadTestFiles.find((file) => file.name === element.name);
            if (!stateAsset) {
            const assets = await assetService.getAssetsFromList(element);
            assetsData.push(assets);
            dispatch(loadTestAllFiles(assets));
        } else {
            assetsData.push(stateAsset);
            dispatch(loadTestAllFiles(stateAsset));
        }
        });
    }
        return assetsData;
    };
}


export function loadOpBlobs(project: IProject ):
(dispatch: Dispatch, getState: () => IApplicationState) => Promise<IAsset[]> {
    return async (dispatch: Dispatch, getState: () => IApplicationState) => {
       // debugger;
        const appState = getState();
        const projectService = new ProjectService();
        const loadFiles = await projectService.loadAllFilesFromOpBlob(project.name);
        const assetService = new AssetService(project);
        const assetsData = [];

        loadFiles.forEach(async (element: any) => {
            const stateAsset = await appState.loadAllFiles.find((file) => file.name === element.name);
            if (!stateAsset) {
            const assets = await assetService.getAssetsFromList(element);
            assetsData.push(assets);
           dispatch(loadAllBlobFiles(assets));
        } else {
            assetsData.push(stateAsset);
            dispatch(loadAllBlobFiles(stateAsset));
        }
        });
/*
        if (keyword === "training") {
        loadFiles.forEach(async (element: any) => {
            const stateAsset = await appState.loadAllFiles.find((file) => file.name === element.name);
            if (!stateAsset) {
            const assets = await assetService.getAssetsFromList(element);
            assetsData.push(assets);
            dispatch(loadAllBlobFiles(assets));
        } else {
            assetsData.push(stateAsset);
            dispatch(loadAllBlobFiles(stateAsset));
        }
        });
    } else {
        loadFiles.forEach(async (element: any) => {
            const stateAsset = appState.loadTestFiles.find((file) => file.name === element.name);
            if (!stateAsset) {
            const assets = await assetService.getAssetsFromList(element);
            assetsData.push(assets);
            dispatch(loadTestAllFiles(assets));
        } else {
            assetsData.push(stateAsset);
            dispatch(loadTestAllFiles(stateAsset));
        }
        });
    }
    */
        return assetsData;
    };
}



/**
 * Get Test files
 */
export function loadTestBlobs(project: IProject, keyword: string):
(dispatch: Dispatch, getState: () => IApplicationState) => Promise<any> {
    return async (dispatch: Dispatch, getState: () => IApplicationState) => {
        const appState = getState();
        const projectService = new ProjectService();
        const testFiles = await projectService.loadAllFilesFromBlob(project.name, keyword);
        const assetService = new AssetService(project);
        const assetsData = [];
        if (testFiles.length > 0) {
            testFiles.forEach(async (element: any) => {
            const stateAsset = appState.loadTestFiles.find((file) => file.name === element.name);
            if (!stateAsset) {
            const assets = await assetService.getAssetsFromList(element);
            assetsData.push(element);
            dispatch(loadTestAllFiles(element));
        } else {
            assetsData.push(stateAsset);
            dispatch(loadTestAllFiles(stateAsset));
        }
        });
    }
        return assetsData;
    };
}

export function deleteAsset(asset: IAsset):
(dispatch: Dispatch) => Promise<void> {
    return async (dispatch: Dispatch) => {
    dispatch(deleteAssetAction(asset));
    }
}

/**
 * Generate Ocr
 */
export function generateOcrForSingleFile(project: IProject, fileName: string, asset: IAsset):
(dispatch: Dispatch, getState: () => IApplicationState) => Promise<void> {
    return async (dispatch: Dispatch, getState: () => IApplicationState) => {
        const appState = getState();
        const ocrContent = await appState.selectedOcrFiles.find((ocrFile) => ocrFile.fileName === fileName);
        let ocrContentData;
        if (!ocrContent) {
        const projectService = new ProjectService();
        ocrContentData = await projectService.generateOcr(project.name, fileName);
        dispatch(loadOcrContent(ocrContentData));
        const assetService = new AssetService(project);
        const updatedAsset = await assetService.getAssetsFromList(asset, "doneOcr");
        dispatch(updateAssetState(updatedAsset));
        } else {
            ocrContentData = ocrContent;
        }
        return ocrContentData;
    };
}
/**
 * Dispatches Save Project action and resolves with IProject
 * @param project - Project to save
 */
export function saveProject(project: IProject, saveTags?: boolean, updateTagsFromFiles?: boolean)
    : (dispatch: Dispatch, getState: () => IApplicationState) => Promise<IProject> {
    return async (dispatch: Dispatch, getState: () => IApplicationState) => {
        const appState = getState();
        const projectService = new ProjectService();

        // if (projectService.isDuplicate(project, appState.recentProjects)) {
        //     throw new AppError(ErrorCode.ProjectDuplicateName, `Project with name '${project.name}
        //         already exists with the same target connection '${project.sourceConnection.name}'`);
        // }

        const projectToken = appState.appSettings.securityTokens
        .find((securityToken) => securityToken.name === project.securityToken);
        // const projectToken = {name: "token", key: project.securityToken};
        // if (!projectToken) {
        //     throw new AppError(ErrorCode.SecurityTokenNotFound, "Security Token Not Found");
        // }

        const savedProject = await projectService.save(project, projectToken, saveTags, updateTagsFromFiles);
        await dispatch(saveProjectAction(savedProject));
        // return Promise.resolve(savedProject);
        // Reload project after save actions
        await loadProject(savedProject)(dispatch, getState);

        return savedProject;
    };
}

export function updateProjectTagsFromFiles(project: IProject, asset?: string): (dispatch: Dispatch) => Promise<void> {
    return async (dispatch: Dispatch) => {
        const projectService = new ProjectService();
        const updatedProject = await projectService.updateProjectTagsFromFiles(project, asset);
        dispatch(updateProjectTagsFromFilesAction(updatedProject));
    };
}

export function updateProjectTagsFromExistingLabelFile(project: IProject, asset?: string): (dispatch: Dispatch) => Promise<void> {
    return async (dispatch: Dispatch) => {
        const projectService = new ProjectService();
        const updatedProject = await projectService.updateProjectTagsFromExistingLabelFile(project, asset);
        dispatch(updateProjectTagsFromFilesAction(updatedProject));
    };
}

/**
 * Dispatches Delete Project action and resolves with project
 * @param project - Project to delete
 */
export function deleteProject(project: IProject)
    : (dispatch: Dispatch, getState: () => IApplicationState) => Promise<void> {
    return async (dispatch: Dispatch, getState: () => IApplicationState) => {
        const appState = getState();
        const projectService = new ProjectService();

        // Lookup security token used to decrypt project settings
        const projectToken = appState.appSettings.securityTokens
            .find((securityToken) => securityToken.name === project.securityToken);

        if (!projectToken) {
            throw new AppError(ErrorCode.SecurityTokenNotFound, "Security Token Not Found");
        }

        const decryptedProject = await projectService.load(project, projectToken);

        await projectService.delete(decryptedProject);
        dispatch(deleteProjectAction(decryptedProject));
    };
}

/**
 * Dispatches Close Project action
 */
export function closeProject(): (dispatch: Dispatch) => void {
    return (dispatch: Dispatch): void => {
        dispatch({ type: ActionTypes.CLOSE_PROJECT_SUCCESS });
    };
}


/**
 * Dispatches Close Project action
 */
export function clearTestBlobs(): (dispatch: Dispatch) => void {
    return (dispatch: Dispatch): void => {
       // dispatch({ type: ActionTypes.CLOSE_PROJECT_SUCCESS });
          dispatch(clearOPTestAllFiles(" "));
    };
}


/**
 * Gets assets from project, dispatches load assets action and returns assets
 * @param project - Project from which to load assets
 */
export function loadAssets(project: IProject): (dispatch: Dispatch) => Promise<IAsset[]> {
    return async (dispatch: Dispatch) => {
        const assetService = new AssetService(project);
        const assets = await assetService.getAssets();

       // dispatch(loadProjectAssetsAction(assets));
            //new code fr preview
            if (!areAssetsEqual(assets, project.assets)) {
                dispatch(loadProjectAssetsAction(assets));
            }
        return assets;
    };
}


/**
 * Gets assets from project, dispatches load assets action and returns assets
 * @param project - Project from which to load assets
 */
export function loadODAssets(project: IProject,assets:IAsset[]): (dispatch: Dispatch) => Promise<IAsset[]> {
    return async (dispatch: Dispatch) => {
      //  const assetService = new AssetService(project);
        //const assets = await assetService.getAssets();

       // dispatch(loadProjectAssetsAction(assets));
            //new code fr preview
          //  if (!areAssetsEqual(assets, project.assets)) {
           //     dispatch(loadProjectAssetsAction(assets));
           // }
            dispatch(loadProjectAssetsAction(assets));
        return assets;
    };
}

/**
 * Load metadata from asset within project
 * @param project - Project from which to load asset metadata
 * @param asset - Asset from which to load metadata
 */
export function loadAssetMetadata(project: IProject, asset: IAsset): (dispatch: Dispatch) => Promise<IAssetMetadata> {
    return async (dispatch: Dispatch) => {
        const assetService = new AssetService(project);
        const assetMetadata = await assetService.getAssetMetadata(asset);
        dispatch(loadAssetMetadataAction(assetMetadata));
        return { ...assetMetadata };
    };
}

/**
 * Save metadata from asset within project
 * @param project - Project from which to save asset metadata
 * @param assetMetadata - Metadata for asset within project
 */
export function saveAssetMetadata(
    project: IProject,
    assetMetadata: IAssetMetadata): (dispatch: Dispatch) => Promise<IAssetMetadata> {
    const newAssetMetadata = { ...assetMetadata, version: appInfo.version };

    return async (dispatch: Dispatch) => {
        const assetService = new AssetService(project);
        const savedMetadata = await assetService.save(newAssetMetadata);
        if(savedMetadata && savedMetadata.labelData && savedMetadata.labelData.labels.length > 0) {
        const updatedAsset = await assetService.getAssetsFromList(assetMetadata.asset, "doneLabel");
        dispatch(updateAssetState(updatedAsset));
        }
        dispatch(saveAssetMetadataAction(savedMetadata));

        return { ...savedMetadata };
    };
}

/**
 * Updates a project and all asset references from oldTagName to newTagName
 * @param project The project to update tags
 * @param oldTag The old tag
 * @param newTag The new tag
 */
export function updateProjectTag(project: IProject, oldTag: ITag, newTag: ITag)
    : (dispatch: Dispatch, getState: () => IApplicationState) => Promise<IAssetMetadata[]> {
    return async (dispatch: Dispatch, getState: () => IApplicationState) => {
        let assetUpdates: IAssetMetadata[] = [];
        const appState = getState();
        const appStateAsssets = appState.loadAllFiles;
        if (oldTag.name !== newTag.name) {
            // Find tags to rename
            const assetService = new AssetService(project);
            assetUpdates = await assetService.renameTag(oldTag.name, newTag.name, appStateAsssets);

            // Save updated assets
            await assetUpdates.forEachAsync(async (assetMetadata) => {
                await saveAssetMetadata(project, assetMetadata)(dispatch);
            });
    }

        const currentProject = getState().currentProject;
        const updatedProject = {
            ...currentProject,
            tags: project.tags.map((t) => (t.name === oldTag.name) ? { ...newTag } : t),
        };

        // Save updated project tags
        await saveProject(updatedProject, true, false)(dispatch, getState);
        dispatch(updateProjectTagAction(updatedProject));

        return assetUpdates;
    };
}

/**
 * Updates a project and all asset references from oldTagName to newTagName
 * @param project The project to update tags
 * @param oldTag The old tag
 * @param newTag The new tag
 */
export function updateProjectTableTag(project: IProject, oldTag: ITableTag, newTag: ITableTag)
    : (dispatch: Dispatch, getState: () => IApplicationState) => Promise<IAssetMetadata[]> {
    return async (dispatch: Dispatch, getState: () => IApplicationState) => {
        let assetUpdates: IAssetMetadata[] = [];

        if (oldTag.name !== newTag.name) {
            // Find tags to rename
            const assetService = new AssetService(project);
            assetUpdates = await assetService.renameTag(oldTag.name, newTag.name);

            // Save updated assets
            await assetUpdates.forEachAsync(async (assetMetadata) => {
                await saveAssetMetadata(project, assetMetadata)(dispatch);
            });
    }

        const currentProject = getState().currentProject;
        const updatedProject = {
            ...currentProject,
            tableTags: project.tableTags.map((t) => (t.name === oldTag.name) ? { ...newTag } : t),
        };

        // Save updated project tags
        await saveProject(updatedProject, true, false)(dispatch, getState);
        dispatch(updateProjectTagAction(updatedProject));

        return assetUpdates;
    };
}
/**
 * Updates a project and all asset references from oldTagName to newTagName
 * @param project The project to delete tags
 * @param tagName The tag to delete
 */
export function deleteProjectTag(project: IProject, tagName)
    : (dispatch: Dispatch, getState: () => IApplicationState) => Promise<IAssetMetadata[]> {
    return async (dispatch: Dispatch, getState: () => IApplicationState) => {
        // Find tags to rename
        const appState = getState();
        const appStateAssets = appState.loadAllFiles;
        const assetService = new AssetService(project);
        const assetUpdates = await assetService.deleteTag(tagName, appStateAssets);

        // Save updated assets
        await assetUpdates.forEachAsync(async (assetMetadata) => {
            await saveAssetMetadata(project, assetMetadata)(dispatch);
        });

        const currentProject = getState().currentProject;
        const updatedProject = {
            ...currentProject,
            tags: project.tags.filter((t) => t.name !== tagName),
        };

        // Save updated project tags
        await saveProject(updatedProject, true, false)(dispatch, getState);
        dispatch(deleteProjectTagAction(updatedProject));

        return assetUpdates;
    };
}
/**
 * Updates a project and all asset references from oldTagName to newTagName
 * @param project The project to delete tags
 * @param tagName The tag to delete
 */
export function deleteTagFromFile(project: IProject, tagName, asset: IAsset)
    : (dispatch: Dispatch, getState: () => IApplicationState) => Promise<IAssetMetadata[]> {
    return async (dispatch: Dispatch, getState: () => IApplicationState) => {
        // Find tags to rename
        const assetService = new AssetService(project);
        const assetUpdates = await assetService.deleteFileTag(tagName, asset);

        // Save updated assets
        await assetUpdates.forEachAsync(async (assetMetadata) => {
            await saveAssetMetadata(project, assetMetadata)(dispatch);
        });

        const currentProject = getState().currentProject;
        const updatedProject = {
            ...currentProject,
            tags: project.tags,
            // tags,
        };

        // Save updated project tags
        await saveProject(updatedProject, true, false)(dispatch, getState);
        dispatch(deleteProjectTagAction(updatedProject));

        return assetUpdates;
    };
}

//new 
/**
 * Dispatches Delete Asset action
 */
export function deleteAssetFR(project: IProject, assetMetadata: IAssetMetadata): (dispatch: Dispatch) => void {
    return async (dispatch: Dispatch) => {
        const assetService = new AssetService(project);
        await assetService.delete(assetMetadata);
        const assets = await assetService.getAssets();
        if (!areAssetsEqual(assets, project.assets)) {
            dispatch(deleteProjectAssetAction(assets));
        }
    };
}


export function updatedAssetMetadata(project: IProject,
    assetDocumentCountDifference: any): (dispatch: Dispatch) => Promise<void> {
    return async (dispatch: Dispatch) => {
        const projectService = new ProjectService();
        const updatedProject = await projectService.updatedAssetMetadata(project, assetDocumentCountDifference);
        if (updatedProject !== project) {
            dispatch(updatedAssetMetadataAction(updatedProject));
        }
    };
}


function areAssetsEqual(assets: IAsset[], projectAssets: { [index: string]: IAsset }): boolean {
    const keys = Object.keys(projectAssets || {});
    if (assets.length !== keys.length) {
        return false;
    }

    const assetsMap = {};
    assets.forEach((asset) => {
        assetsMap[asset.id] = asset;
    });

    return JSON.stringify(assetsMap) === JSON.stringify(projectAssets);
}




//new

   
/**
 * Initialize export provider, get export data and dispatch export project action
 * @param project - Project to export
 */
export function exportProject(project: IProject)
: (dispatch: Dispatch, getState:()=> IApplicationState) => Promise<void> | Promise<IExportResults> {
   
    return async (dispatch: Dispatch, getState:()=>IApplicationState) => {
        if (!project.exportFormat) {
            throw new AppError(ErrorCode.ExportFormatNotFound, strings.errors.exportFormatNotFound.message);
        }
      
         // left export functionality from AZURE VOTT start

         //if (project.projType === 'od') {

            const  providerType = "azureCustomVision";
       
            const azureexportOptions : IAzureCustomVisionExportOptions ={
                assetState: ExportAssetState.Visited,
                newOrExisting: NewOrExisting.New,
              //  region: AzureRegion.EastUS2,
               // apiKey: "8c8eef4257f0401fa0a50064bf00d79d",
               //region: AzureRegion.EastUS,
               //apiKey: "7d70a928b45c4ee184f9dee2b1b33e9d",

              // region: AzureRegion.EastUS2,
             //  region: AzureRegion.EastUS,
              // apiKey : "7d70a928b45c4ee184f9dee2b1b33e9d",
              // apiKey: "8b8ba0c2da2046e08a3110281f35f0a5",
              //apiKey : "fad20d4906df4906afa20cccfd969fe5",
             // apiKey : "1d0f5d93322c49438e3c6b351bfec8a8",
                projectId: "",
                name: `${project.name}-export1`,
                description: "project export",
               // projectType: "ObjectDetection",
                classificationType: "Multilabel",
              //  domainId: "da2e3a8a-40a5-4171-82f4-58522f70fbc1",
              //  domainId: "b4ccfcd8-3333-4b8f-a3ce-813378a34fb1",

              region: AzureRegion.EastUS,
             // apiKey : "7d70a928b45c4ee184f9dee2b1b33e9d",             
             // domainId: "da2e3a8a-40a5-4171-82f4-58522f70fbc1",

             apiKey :IAzureCVExportFields.apiKey,
             domainId:IAzureCVExportFields.domainId,
               };
               
               const customExportOption: IExportFormat = {
                providerType:providerType,
                providerOptions:  azureexportOptions
               };
       
              
      
       //        await this.loadProject(project);
      
            // check for the proj id - if exists call export else make new proj
            if(!project.cvProjId)
            {
//  projectId: "74a31d81-c30e-4e31-b355-02dfde3b1a7b",          
                        
        const azurepro = new AzureCustomVisionProvider(project, azureexportOptions);       
        //if save success then we need to store the id in one common file
      const result = await  azurepro.save(customExportOption);      
      console.log("result",result.projectId);
      customExportOption.providerOptions.projectId = result.projectId;       
      //save this id in fileds. json
      //project.exportFormat = customExportOption;
     //project.cvProjId = result.projectId;
     //project.cvProjName = azureexportOptions.name;  
           //
       const currentProject = getState().currentProject;
       const updatedProject = {
           ...currentProject,
           cvProjId : result.projectId,
           cvProjName : azureexportOptions.name,          
           exportFormat: customExportOption,
       };
       // Save updated project tags
       await saveProject(updatedProject, true, false)(dispatch, getState);    
       await dispatch(updateProjectsuccessAction(updatedProject));
       
       const azureproexp = new AzureCustomVisionProvider(updatedProject, azureexportOptions);       
      
          const results = await azureproexp.export();         
          return results as IExportResults;
      // }     
       //
    }
    else
    {
azureexportOptions.projectId = project.cvProjId; 
     azureexportOptions.name =project.cvProjName;

        const customExportOption: IExportFormat = {
            providerType:providerType,
            providerOptions:  azureexportOptions
           };

      customExportOption.providerOptions.projectId = project.cvProjId; 
       const currentProject = getState().currentProject;
       const updatedProject = {
           ...currentProject,
           exportFormat: customExportOption,
       };

       // Save updated project tags
       await saveProject(updatedProject, true, false)(dispatch, getState);
      // await this.saveProject(project, false, false);
       await dispatch(updateProjectsuccessAction(updatedProject));           
        const azurepro = new AzureCustomVisionProvider(updatedProject, azureexportOptions);       
        if (updatedProject.exportFormat && updatedProject.exportFormat.providerType) {       
            const results = await azurepro.export();
            return results as IExportResults;
         } 
    }

    
     
             
         //// left export functionality from AZURE VOTT end

/*
        if (project.exportFormat && project.exportFormat.providerType) {
          /*  const exportProvider = ExportProviderFactory.create(
                project.exportFormat.providerType,
                project,
                project.exportFormat.providerOptions);*/

           // const results = await exportProvider.export();
           /* const results = await azurepro.export();
            dispatch(exportProjectAction(project));

            return results as IExportResults;
        }*/
    };
}


 
//
/**
 * Dispatches Save Project action and resolves with IProject
 * @param project - Project to save
 */
export function saveTrainedProject(project: IProject,trainingInfo?: any)
    : (dispatch: Dispatch, getState: () => IApplicationState) => Promise<IProject> {
    return async (dispatch: Dispatch, getState: () => IApplicationState) => {
      
        const appState = getState();
        const projectService = new ProjectService();

        const currentProject = getState().currentProject;
        const updatedProject = {
            ...currentProject,
            trainingInfo: trainingInfo,
        };
 
        // Save updated project training data
        const savedProject =  await saveProject(updatedProject, true, false)(dispatch, getState);
       
        
        await dispatch(updateProjecttrainsuccessAction(updatedProject)); 
      //  await dispatch(updateProjectsuccessAction(updatedProject));           
         
          return savedProject;
     }
    };


//


// new start
export function updateProjectcv(project: IProject, cvProjId: string, cvProjName: string)
    : (dispatch: Dispatch, getState: () => IApplicationState) => Promise<IProject> {
    return async (dispatch: Dispatch, getState: () => IApplicationState) => {
      
       /* let assetUpdates: IAssetMetadata[] = [];
        const appState = getState();
        const appStateAsssets = appState.loadAllFiles;
        if (oldTag.name !== newTag.name) {
            // Find tags to rename
            const assetService = new AssetService(project);
            assetUpdates = await assetService.renameTag(oldTag.name, newTag.name, appStateAsssets);

            // Save updated assets
            await assetUpdates.forEachAsync(async (assetMetadata) => {
                await saveAssetMetadata(project, assetMetadata)(dispatch);
            });
    }*/
   // project.cvProjId = result.projectId;
    //project.cvProjName = azureexportOptions.name;
        const currentProject = getState().currentProject;
        const updatedProject = {
            ...currentProject,
            cvProjId:cvProjId,
            cvProjName:cvProjName,           
        };

        // Save updated project tags
     const savedProject =   await saveProject(updatedProject, true, false)(dispatch, getState);
      //  dispatch(updateProjectTagAction(updatedProject));

        await dispatch(saveProjectAction(savedProject));
        // return Promise.resolve(savedProject);
        // Reload project after save actions
        await loadProject(savedProject)(dispatch, getState);

        return savedProject;

       // return assetUpdates;
    };
}

/**
 * add asset, ocr data, labels to project storage.
 */
export function addAssetToProject(project: IProject, fileName: string, buffer: Buffer, analyzeResult: any): (dispatch: Dispatch) => Promise<IAsset> {
    return async (dispatch: Dispatch) => {
        const assetService = new AssetService(project);
        await assetService.uploadBuffer(fileName, buffer);
        const assets = await assetService.getAssets();
        const assetName = project.folderPath ? `${project.folderPath}/${fileName}` : fileName;
        const asset = assets.find(a => a.name === assetName);

        await assetService.syncAssetPredictResult(asset, analyzeResult);
        dispatch(addAssetToProjectAction(asset));
        return asset;
    };
}


export interface IUploadDocsData extends IPayloadAction<string, IUploadFilesDetails[]> {
    type: ActionTypes.UPLOAD_DOCS_DETAILS;
}

/**
 * Load project action type
 */
export interface ILoadProjectAction extends IPayloadAction<string, IProject> {
    type: ActionTypes.LOAD_PROJECT_SUCCESS;
}

export interface IUpdateProjectTagsFromFilesAction extends IPayloadAction<string, IProject> {
    type: ActionTypes.UPDATE_PROJECT_TAGS_FROM_FILES_SUCCESS;
}

/**
 * Close project action type
 */
export interface ICloseProjectAction extends Action<string> {
    type: ActionTypes.CLOSE_PROJECT_SUCCESS;
}

/**
 * Save project action type
 */
export interface ISaveProjectAction extends IPayloadAction<string, IProject> {
    type: ActionTypes.SAVE_PROJECT_SUCCESS;
}

/**
 * Delete project action type
 */
export interface IDeleteProjectAction extends IPayloadAction<string, IProject> {
    type: ActionTypes.DELETE_PROJECT_SUCCESS;
}

/**
 * Load project assets action type
 */
export interface ILoadProjectAssetsAction extends IPayloadAction<string, IAsset[]> {
    type: ActionTypes.LOAD_PROJECT_ASSETS_SUCCESS;
}

/**
 * Load asset metadata action type
 */
export interface ILoadAssetMetadataAction extends IPayloadAction<string, IAssetMetadata> {
    type: ActionTypes.LOAD_ASSET_METADATA_SUCCESS;
}

/**
 * Save asset metadata action type
 */
export interface ISaveAssetMetadataAction extends IPayloadAction<string, IAssetMetadata> {
    type: ActionTypes.SAVE_ASSET_METADATA_SUCCESS;
}

/**
 * Update Project Tag action type
 */
export interface IUpdateProjectTagAction extends IPayloadAction<string, IProject> {
    type: ActionTypes.UPDATE_PROJECT_TAG_SUCCESS;
}

/**
 * Delete project tag action type
 */
export interface IDeleteProjectTagAction extends IPayloadAction<string, IProject> {
    type: ActionTypes.DELETE_PROJECT_TAG_SUCCESS;
}

/**
 * Load Ocr Content
 */
export interface ILoadOcrContentAction extends IPayloadAction<string, IOcrContent> {
    type: ActionTypes.LOAD_OCR_CONTENT_SUCCESS;
}

export interface ILoadBlobFilesAction extends IPayloadAction<string, IAsset> {
    type: ActionTypes.LOAD_ALL_BLOB_FILES;
}

export interface IUpdateAssetWithBlobAction extends IPayloadAction<string, IAsset> {
    type: ActionTypes.UPDATE_ASSET_WITH_BLOB_SUCCESS;
}

export interface IClearALlOcrContentsExistingModel extends Action<string> {
    type: ActionTypes.CLEAR_EXISTING_MODEL_OCR_FILES_SUCCESS;
}

export interface IClearALlExistingTestFiles extends Action<string> {
    type: ActionTypes.CLEAR_EXISTING_TEST_FILES_SUCCESS;
}

export interface ILoadTestFilesAction extends IPayloadAction<string, any> {
    type: ActionTypes.LOAD_TEST_ALL_FILES;
}

export interface ILoadOPTestFilesAction extends IPayloadAction<string, any> {
    type: ActionTypes.LOAD_OP_TEST_ALL_FILES;
}

export interface IClearOPTestFilesAction extends IPayloadAction<string, any> {
    type: ActionTypes.DELETE_OP_TEST_ALL_FILES;
}

export interface IUpdateAssetStateAction extends IPayloadAction<string, any> {
    type: ActionTypes.UPDATE_ASSET_STATE_SUCCESS;
}

export interface IUpdateAssetWithImageUriStateAction extends IPayloadAction<string, any> {
    type: ActionTypes.UPDATE_ASSET_STATE_WITH_IMAGE_URI_SUCCESS;
}

export interface IDeleteAssetAction extends IPayloadAction<string, IAsset> {
    type: ActionTypes.DELETE_ASSET_SUCCESS;
}

/**
 * Clear Ocr Contents
 */

 // clearExistiongOcrFiles

/**
 * Instance of Load Project action
 */ 
export const updateAssetState = createPayloadAction<IUpdateAssetStateAction>(ActionTypes.UPDATE_ASSET_STATE_SUCCESS);
export const updateAssetWithImageUriAction = createPayloadAction<IUpdateAssetWithImageUriStateAction>(ActionTypes.UPDATE_ASSET_STATE_WITH_IMAGE_URI_SUCCESS);
export const uploadedDocsData = createPayloadAction<IUploadDocsData>(ActionTypes.UPLOAD_DOCS_DETAILS);
export const loadAllBlobFiles = createPayloadAction<ILoadBlobFilesAction>(ActionTypes.LOAD_ALL_BLOB_FILES);
export const loadTestAllFiles = createPayloadAction<ILoadTestFilesAction>(ActionTypes.LOAD_TEST_ALL_FILES);
//NEW
export const loadOPTestAllFiles = createPayloadAction<ILoadOPTestFilesAction>(ActionTypes.LOAD_OP_TEST_ALL_FILES);
export const clearOPTestAllFiles = createPayloadAction<IClearOPTestFilesAction>(ActionTypes.DELETE_OP_TEST_ALL_FILES);


export const updateAssetWithBlob = createPayloadAction<IUpdateAssetWithBlobAction>(ActionTypes.UPDATE_ASSET_WITH_BLOB_SUCCESS);
export const deleteAssetAction = createPayloadAction<IDeleteAssetAction>(ActionTypes.DELETE_ASSET_SUCCESS);

export const clearExistiongOcrFiles = createAction<IClearALlOcrContentsExistingModel>(ActionTypes.CLEAR_EXISTING_MODEL_OCR_FILES_SUCCESS);
export const clearExistingTestFiles = createAction<IClearALlExistingTestFiles>(ActionTypes.CLEAR_EXISTING_TEST_FILES_SUCCESS);

/**
 * Instance of Load Project action
 */
export const loadProjectAction = createPayloadAction<ILoadProjectAction>(ActionTypes.LOAD_PROJECT_SUCCESS);
/**
 * Instance of Close Project action
 */
export const closeProjectAction = createAction<ICloseProjectAction>(ActionTypes.CLOSE_PROJECT_SUCCESS);
/**
 * Instance of Save Project action
 */
export const saveProjectAction = createPayloadAction<ISaveProjectAction>(ActionTypes.SAVE_PROJECT_SUCCESS);
/**
 * Instance of Delete Project action
 */
export const deleteProjectAction = createPayloadAction<IDeleteProjectAction>(ActionTypes.DELETE_PROJECT_SUCCESS);
/**
 * Instance of Load Project Assets action
 */
export const loadProjectAssetsAction =
    createPayloadAction<ILoadProjectAssetsAction>(ActionTypes.LOAD_PROJECT_ASSETS_SUCCESS);
/**
 * Instance of Load Asset Metadata action
 */
export const loadAssetMetadataAction =
    createPayloadAction<ILoadAssetMetadataAction>(ActionTypes.LOAD_ASSET_METADATA_SUCCESS);
/**
 * Instance of Save Asset Metadata action
 */
export const saveAssetMetadataAction =
    createPayloadAction<ISaveAssetMetadataAction>(ActionTypes.SAVE_ASSET_METADATA_SUCCESS);
/**
 * Instance of Update project tag action
 */
export const updateProjectTagAction =
    createPayloadAction<IUpdateProjectTagAction>(ActionTypes.UPDATE_PROJECT_TAG_SUCCESS);

export const updateProjectTagsFromFilesAction =
    createPayloadAction<IUpdateProjectTagsFromFilesAction>(ActionTypes.UPDATE_PROJECT_TAGS_FROM_FILES_SUCCESS);
/**
 * Instance of Delete project tag action
 */
export const deleteProjectTagAction =
    createPayloadAction<IDeleteProjectTagAction>(ActionTypes.DELETE_PROJECT_TAG_SUCCESS);
/**
 * Load Ocr Content
 */
export const loadOcrContent =
    createPayloadAction<ILoadOcrContentAction>(ActionTypes.LOAD_OCR_CONTENT_SUCCESS);


    
export interface IUpdateTagDocumentCount extends IPayloadAction<string, IProject> {
    type: ActionTypes.UPDATE_TAG_LABEL_COUNTS_SUCCESS;
}

    
export const updatedAssetMetadataAction =
createPayloadAction<IUpdateTagDocumentCount>(ActionTypes.UPDATE_TAG_LABEL_COUNTS_SUCCESS);



/**
 * Delete project asset action type
 */
export interface IDeleteProjectAssetAction extends IPayloadAction<string, IAsset[]> {
    type: ActionTypes.DELETE_PROJECT_ASSET_SUCCESS;
}

/**
 * Instance of Delete Project Asset action
 */
export const deleteProjectAssetAction =
    createPayloadAction<IDeleteProjectAssetAction>(ActionTypes.DELETE_PROJECT_ASSET_SUCCESS);


/**
 * Export project action type
 */
export interface IExportProjectAction extends IPayloadAction<string, IProject> {
    type: ActionTypes.EXPORT_PROJECT_SUCCESS;
}

/**
 * Instance of Export Project action
 */
export const exportProjectAction =
    createPayloadAction<IExportProjectAction>(ActionTypes.EXPORT_PROJECT_SUCCESS);


    
/**
 * Instance of Export Project action
 */
export interface IProjectUpdateSuccessAction extends IPayloadAction<string, IProject> {
    type: ActionTypes.UPDATE_PROJECT_SUCCESS;
}

 export const updateProjectsuccessAction =
createPayloadAction<IProjectUpdateSuccessAction>(ActionTypes.UPDATE_PROJECT_SUCCESS);



/**
 * Instance of Export Project action
 */
export interface IProjectUpdateTrainSuccessAction extends IPayloadAction<string, IProject> {
    type: ActionTypes.UPDATE_PROJECT_TRAIN_SUCCESS;
}


export const updateProjecttrainsuccessAction =
createPayloadAction<IProjectUpdateTrainSuccessAction>(ActionTypes.UPDATE_PROJECT_TRAIN_SUCCESS);


/**
 * Add asset to project action type
 */
export interface IAddAssetToProjectAction extends IPayloadAction<string, IAsset> {
    type: ActionTypes.ADD_ASSET_TO_PROJECT_SUCCESS;
}
/**
 * Instance of Add Asset to Project action
 */
export const addAssetToProjectAction = createPayloadAction<IAddAssetToProjectAction>(ActionTypes.ADD_ASSET_TO_PROJECT_SUCCESS);
